import { ACTION_BAR_BUTTON_NAMES_MAP } from "lib/constants";

export type KerningTarget =
  | { kind: "textbox"; itemId: string; pageId: string }
  | { kind: "table2Cell"; itemId: string; pageId: string; cellId: string };

interface ResolveProps {
  context?: {
    isInTextEditMode?: boolean;
    selectedTable2CellIds?: string[];
  };
  actionbar?: {
    buttonActive?: string | null;
  };
  selectedItems?: Array<{ itemId: string; pageId: string }>;
  designData?: {
    elements?: Record<string, unknown>;
  };
}

const TABLE2_TEXTFIELD = ACTION_BAR_BUTTON_NAMES_MAP.TABLE2_TEXTFIELD;

export const resolveKerningTarget = (
  props: ResolveProps | null | undefined
): KerningTarget | null => {
  if (!props) return null;
  const context = props.context;
  const actionbar = props.actionbar;
  const isInTextEditMode = Boolean(context && context.isInTextEditMode);
  const isCellTextField = Boolean(
    actionbar && actionbar.buttonActive === TABLE2_TEXTFIELD
  );
  if (!isInTextEditMode && !isCellTextField) return null;

  const item = props.selectedItems && props.selectedItems[0];
  if (!item) return null;
  const elements = (props.designData && props.designData.elements) || {};
  const element = elements[item.itemId] as
    | {
        type?: string;
        cells?: Record<string, unknown>;
        restrictions?: string[];
      }
    | undefined;
  if (!element) return null;
  if (
    Array.isArray(element.restrictions) &&
    element.restrictions.includes("letterSpacing")
  ) {
    return null;
  }

  if (element.type === "textbox" && isInTextEditMode) {
    return { kind: "textbox", itemId: item.itemId, pageId: item.pageId };
  }

  if (element.type === "table2" && isCellTextField) {
    const selectedCellIds = (context && context.selectedTable2CellIds) || [];
    if (selectedCellIds.length !== 1) return null;
    const cellId = selectedCellIds[0];
    if (!cellId) return null;
    if (!element.cells || !element.cells[cellId]) return null;
    return {
      kind: "table2Cell",
      itemId: item.itemId,
      pageId: item.pageId,
      cellId
    };
  }

  return null;
};

// Locate the contenteditable owned by a kerning target. Textbox elements
// render their root with `id={uniqueId}`; Table2 cells render the contained
// cell with `id={'cell-' + cellId}` (the table root itself uses a prefixed
// id, hence the kind-specific lookup). Returns null when the host is missing
// or has no contenteditable child.
export const findKerningContentEditable = (
  target: KerningTarget
): HTMLElement | null => {
  if (typeof document === "undefined") return null;
  const host =
    target.kind === "table2Cell"
      ? document.getElementById(`cell-${target.cellId}`)
      : document.getElementById(target.itemId);
  if (!host) return null;
  return host.querySelector<HTMLElement>('[contenteditable="true"]');
};
